<template>
  <collapse-card :header="true" :title="title" :open="false">
    <template v-if="showAutoSelect" v-slot:left-header>
      <div class="block">
        <b-radio v-model="autoSelect"
          native-value="Minimal">
          Minimal
        </b-radio>
        <b-radio v-model="autoSelect"
          native-value="Everything">
          Everything
        </b-radio>
        <b-radio v-model="autoSelect"
          native-value="Custom">
          Custom
        </b-radio>
      </div>
    </template>
    <tiles :wrap="true">
      <tile-basic
        v-for="(obj,key) in dataFields"
        :key="key"
        :title="obj.def.label"
        class="tile is-parent is-3 is-grey">
        <template slot="top">
          <p>Included Data Attributes:</p>
          <div class="field tags">
            <b-tag
              v-for="field in obj.included"
              :key="field.label"
              :closable="field.coding !== 'base'"
              @close="removeAttrClick(dataFields[key],field)"
              v-tooltip="{
                    trigger: 'hover',
                    offset: 5,
                    placement: 'top',
                    content: field.description }">
              {{ field.label }}
            </b-tag>
          </div>
          <p>Excluded Data Attributes:</p>
          <div class="field tags">
            <b-tag
              v-for="field in obj.excluded"
              :key="field.label"
              class="add"
              :closable="field.coding !== 'base'"
              @close="addAttrClick(dataFields[key],field)"
              v-tooltip="{
                    trigger: 'hover',
                    offset: 5,
                    placement: 'top',
                    content: field.description }">
              {{ field.label }}
            </b-tag>
          </div>
        </template>
      </tile-basic>
    </tiles>
  </collapse-card>
</template>

<script>
import _ from 'lodash';
import CollapseCard from '@/components/CollapseCard.vue';
import Tiles from '@/components/Tiles.vue';
import TileBasic from '@/components/TileBasic.vue';

export default {
  name: 'DataConfigsForm',
  components: {
    CollapseCard,
    Tiles,
    TileBasic,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    showAutoSelect: {
      type: Boolean,
      default: true,
    },
  },
  beforeMount() {
    // This builds up the list of included and excluded attributes for each type (e.g. fsEvent)
    Object.keys(this.data).forEach((key) => {
      const def = this.mappings[key];

      const dataFields = {
        def,
        included: this.getBaseFields(def),
        excluded: [],
      };

      // Fill in excluded or included based on the passed in attr array
      this.getExtendedFields(def).forEach((field) => {
        if (this.data[key].attrs.includes(field.value)) {
          dataFields.included.push(field);
        } else {
          dataFields.excluded.push(field);
        }
      });

      // Fill in excluded or included based on the passed in related array
      this.getRelatedFields(def).forEach((field) => {
        if (this.data[key].related.includes(field.type)) {
          dataFields.included.push(field);
        } else {
          dataFields.excluded.push(field);
        }
      });

      // Need to use $set to make it reactive
      this.$set(this.dataFields, key, dataFields);
    });
  },
  mounted() {
    this.autoSelect = 'Everything';
  },
  watch: {
    dataInternal: {
      deep: true,
      handler() {
        this.$emit('update:data', this.dataInternal);
      },
    },
    autoSelect() {
      if (this.showAutoSelect) {
        if (this.autoSelect === 'Minimal') {
          this.selectMinimal();
        } else if (this.autoSelect === 'Everything') {
          this.selectEverything();
        }
      }
    },
  },
  data() {
    return {
      dataFields: {},
      mappings: this.$typeDefs.mappings,
      autoSelect: 'Minimal',
      defs: this.$typeDefs,
    };
  },
  computed: {
    computedData() {
      const data = {};
      Object.keys(this.dataFields).forEach((key) => {
        data[key] = {
          attrs: [],
          related: [],
        };
        this.dataFields[key].included.forEach((field) => {
          if (field.coding === 'extended') {
            // Extended fields are set by value
            data[key].attrs.push(field.value);
          } else if (field.coding === 'related') {
            // Related fields are set by type
            data[key].related.push(field.type);
          }
        });
      });
      return data;
    },
  },
  methods: {
    getBaseFields(def) {
      return _.filter(def.fields, { coding: 'base' });
    },
    getExtendedFields(def) {
      return _.filter(def.fields, { coding: 'extended' });
    },
    getRelatedFields(def) {
      return _.filter(def.fields, { coding: 'related' });
    },
    removeAttrClick(obj, field) {
      this.autoSelect = 'Custom';
      this.handleDataFieldRemove(obj, field);
    },
    addAttrClick(obj, field) {
      this.autoSelect = 'Custom';
      this.handleDataFieldAdd(obj, field);
    },
    handleDataFieldRemove(obj, field) {
      const index = _.findIndex(obj.included, field);
      obj.included.splice(index, 1);
      obj.excluded.push(field);
      this.$emit('update:data', this.computedData);
    },
    handleDataFieldAdd(obj, field) {
      const index = _.findIndex(obj.excluded, field);
      obj.excluded.splice(index, 1);
      obj.included.push(field);
      this.$emit('update:data', this.computedData);
    },
    selectEverything() {
      Object.values(this.dataFields).forEach((obj) => {
        // Make a copy of items to remove from excluded array
        const excluded = obj.excluded.slice();
        excluded.forEach(field => this.handleDataFieldAdd(obj, field));
      });
    },
    selectMinimal() {
      Object.values(this.dataFields).forEach((obj) => {
        const included = _.filter(obj.included, field => field.coding !== 'base');
        included.forEach(field => this.handleDataFieldRemove(obj, field));
      });
    },
  },
};
</script>

<style lang="scss" scoped>

.tags {
  .tag {
    background-color: #789;
  }
}

</style>
